import { useMemo, useState, type FC } from "react";
import { useQuery } from "@apollo/client";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import UserOrganizationsListQuery from "../graphql/queries/UserOrganizationsListQuery.graphql";
import { OrganizationsAutocompleteQuery } from "@/admin/entities/organizations";
import type {
  OrganizationsAutocompleteQueryType,
  OrganizationsAutocompleteQueryVariablesType,
  UserOrganizationsListQueryType,
  UserOrganizationsListQueryVariablesType,
} from "@/types/graphql/admin.generated";

interface OrganizationOption {
  id: string;
  title: string;
}

interface UserOrganizationAutocompleteProps {
  /** Id редактируемого юзера — опции строятся из ЕГО членств, не всех организаций. */
  userId: string | undefined;
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  disabled?: boolean;
  /**
   * Уже выбранная организация (из запроса редактируемого юзера) — чтобы
   * значение отображалось до того, как список членств этого юзера загрузится.
   */
  initialOrganization?: OrganizationOption | null;
  /**
   * Служебный админ (isAdmin/isSuperAdmin) в партнёрском кабинете проходит
   * PartnerPolicy без членства, и бэкенд (UserService.beforeUpdate) даёт
   * ему любую currentOrganizationId. Для такого юзера список строится из
   * всех организаций, а не из его членств.
   */
  anyOrganization?: boolean;
}

/**
 * Автокомплит выбора "текущей" организации юзера — ТОЛЬКО из реальных
 * членств этого юзера (organizationUsers(filter:{userId})), а не из всех
 * организаций. Тот же запрос, что использует UserOrganizations (вкладка
 * "Организации" в форме юзера) — UserOrganizationsListQuery уже возвращает
 * organization{id,title} на каждом node, в отличие от OrganizationUsersListQuery
 * (используется для списка "Сотрудники" организации и отдаёт user, а не
 * organization).
 * Бэкенд-гвард (UserService.beforeUpdate, для не-админов) отклонит
 * currentOrganizationId, не совпадающий с реальным членством — этот
 * компонент не даёт админу выбрать заведомо невалидный вариант.
 */
export const UserOrganizationAutocomplete: FC<
  UserOrganizationAutocompleteProps
> = ({
  userId,
  value,
  onChange,
  label,
  disabled,
  initialOrganization,
  anyOrganization = false,
}) => {
  const [inputValue, setInputValue] = useState("");

  const memberships = useQuery<
    UserOrganizationsListQueryType,
    UserOrganizationsListQueryVariablesType
  >(UserOrganizationsListQuery, {
    variables: { rowsPerPage: 100, filter: userId ? { userId } : undefined },
    skip: !userId || anyOrganization,
  });

  const allOrganizations = useQuery<
    OrganizationsAutocompleteQueryType,
    OrganizationsAutocompleteQueryVariablesType
  >(OrganizationsAutocompleteQuery, {
    variables: { input: inputValue, value: value ? [value] : [] },
    skip: !userId || !anyOrganization,
  });

  const loading = anyOrganization
    ? allOrganizations.loading
    : memberships.loading;

  const options = useMemo<OrganizationOption[]>(() => {
    const loaded: OrganizationOption[] = anyOrganization
      ? (allOrganizations.data?.autocomplete.options ?? [])
          .filter((opt): opt is { value: string; label: string } =>
            Boolean(opt)
          )
          .map((opt) => ({ id: opt.value, title: opt.label }))
      : (memberships.data?.organizationUsers?.nodes ?? [])
          .map((node) => node.organization)
          .filter((org): org is OrganizationOption => Boolean(org));
    if (
      initialOrganization &&
      value === initialOrganization.id &&
      !loaded.some((org) => org.id === initialOrganization.id)
    ) {
      return [initialOrganization, ...loaded];
    }
    return loaded;
  }, [
    anyOrganization,
    allOrganizations.data,
    memberships.data,
    initialOrganization,
    value,
  ]);

  const selectedOption =
    options.find((org) => org.id === value) ||
    (value && initialOrganization?.id === value ? initialOrganization : null) ||
    null;

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_event, newValue) => onChange(newValue ? newValue.id : null)}
      options={options}
      loading={loading}
      loadingText="Загрузка..."
      noOptionsText={
        anyOrganization
          ? "Организации не найдены"
          : "У пользователя нет организаций"
      }
      inputValue={inputValue}
      onInputChange={(_event, newInput) => setInputValue(newInput)}
      filterOptions={anyOrganization ? (opts) => opts : undefined}
      disabled={disabled || !userId}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      renderInput={(params) => (
        <TextField {...params} label={label} fullWidth />
      )}
    />
  );
};
